/*
 * Copyright 2021 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
"use strict";

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }

function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }

function _awaitAsyncGenerator(value) { return new _AwaitValue(value); }

function _wrapAsyncGenerator(fn) { return function () { return new _AsyncGenerator(fn.apply(this, arguments)); }; }

function _AsyncGenerator(gen) { var front, back; function send(key, arg) { return new Promise(function (resolve, reject) { var request = { key: key, arg: arg, resolve: resolve, reject: reject, next: null }; if (back) { back = back.next = request; } else { front = back = request; resume(key, arg); } }); } function resume(key, arg) { try { var result = gen[key](arg); var value = result.value; var wrappedAwait = value instanceof _AwaitValue; Promise.resolve(wrappedAwait ? value.wrapped : value).then(function (arg) { if (wrappedAwait) { resume(key === "return" ? "return" : "next", arg); return; } settle(result.done ? "return" : "normal", arg); }, function (err) { resume("throw", err); }); } catch (err) { settle("throw", err); } } function settle(type, value) { switch (type) { case "return": front.resolve({ value: value, done: true }); break; case "throw": front.reject(value); break; default: front.resolve({ value: value, done: false }); break; } front = front.next; if (front) { resume(front.key, front.arg); } else { back = null; } } this._invoke = send; if (typeof gen.return !== "function") { this.return = undefined; } }

_AsyncGenerator.prototype[typeof Symbol === "function" && Symbol.asyncIterator || "@@asyncIterator"] = function () { return this; };

_AsyncGenerator.prototype.next = function (arg) { return this._invoke("next", arg); };

_AsyncGenerator.prototype.throw = function (arg) { return this._invoke("throw", arg); };

_AsyncGenerator.prototype.return = function (arg) { return this._invoke("return", arg); };

function _AwaitValue(value) { this.wrapped = value; }

function _asyncIterator(iterable) { var method; if (typeof Symbol !== "undefined") { if (Symbol.asyncIterator) method = iterable[Symbol.asyncIterator]; if (method == null && Symbol.iterator) method = iterable[Symbol.iterator]; } if (method == null) method = iterable["@@asyncIterator"]; if (method == null) method = iterable["@@iterator"]; if (method == null) throw new TypeError("Object is not async iterable"); return method.call(iterable); }

const {
  AsyncGeneratorFunction
} = require("../generator/function");

const {
  isFileProtocol,
  streamToBuffer
} = require("../util");

const {
  issuePut,
  streamGet
} = require("../fetch");

const {
  retry
} = require("../retry");

const {
  HTTP
} = require("../constants");

const {
  TransferEvents
} = require("../controller/transfercontroller");

const MAX_MEMORY_BUFFER = 100 * 1024 * 1024;
/**
 * @typedef {Object} TransferOptions
 *
 * @property {Number} [timeout=3000] Optional socket timeout
 * @property {Number} [retryMaxCount] number of retry attempts, overrides retryMaxDuration
 * @property {Number} [retryMaxDuration=60000] time to retry until throwing an error (ms)
 * @property {Number} [retryInterval=100] time between retries, used by exponential backoff (ms)
 * @property {Boolean} [retryEnabled=true] retry on failure enabled
 * @property {Boolean} [retryAllErrors=false] whether or not to retry on all http error codes or just >=500
 */

/**
 * Transfer parts
 */

class Transfer extends AsyncGeneratorFunction {
  /**
   * Construct a transfer function
   * 
   * @param {RandomFileAccess} randomFileAccess Random file access instance
   * @param {TransferOptions} [options] Transfer options
   */
  constructor(randomFileAccess, options) {
    super();
    this.randomFileAccess = randomFileAccess;
    this.options = options;
  }
  /**
   * Transfer a part
   * 
   * @param {TransferPart} transferPart Part to transfer
   * @param {TransferController} controller Transfer controller
   * @returns {TransferPart} 
   */


  execute(transferParts, controller) {
    var _this = this;

    return _wrapAsyncGenerator(function* () {
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;

      var _iteratorError;

      try {
        for (var _iterator = _asyncIterator(transferParts), _step, _value; _step = yield _awaitAsyncGenerator(_iterator.next()), _iteratorNormalCompletion = _step.done, _value = yield _awaitAsyncGenerator(_step.value), !_iteratorNormalCompletion; _iteratorNormalCompletion = true) {
          const transferPart = _value;

          try {
            controller.notify(TransferEvents.TRANSFER, _this.name, transferPart);
            const targetUrl = Array.isArray(transferPart.targetUrls) && transferPart.targetUrls.length === 1 && transferPart.targetUrls[0];
            const contentRanges = transferPart.contentRange.subranges();

            if (contentRanges.length !== 1) {
              throw Error(`'contentRanges' must be a single range: ${contentRanges}`);
            }

            const contentRange = contentRanges[0];

            if (contentRange.length > MAX_MEMORY_BUFFER) {
              throw Error(`'contentRange.length' too large, not supported yet: ${transferPart.source.url}`);
            }

            if (isFileProtocol(transferPart.source.url) && targetUrl) {
              const buf = yield _awaitAsyncGenerator(_this.randomFileAccess.read(transferPart.source.url, contentRange));
              yield _awaitAsyncGenerator(retry( /*#__PURE__*/_asyncToGenerator(function* () {
                yield issuePut(targetUrl, {
                  body: buf,
                  timeout: _this.options && _this.options.timeout,
                  headers: Object.assign({
                    [HTTP.HEADER.CONTENT_LENGTH]: buf.length,
                    [HTTP.HEADER.CONTENT_TYPE]: transferPart.metadata.contentType
                  }, transferPart.targetHeaders)
                });
              }), _this.options));
            } else if (transferPart.source.blob && targetUrl) {
              const blob = transferPart.source.blob.slice(contentRange.low, contentRange.high + 1);
              yield _awaitAsyncGenerator(retry( /*#__PURE__*/_asyncToGenerator(function* () {
                yield issuePut(targetUrl, {
                  body: blob,
                  timeout: _this.options && _this.options.timeout,
                  headers: Object.assign({
                    [HTTP.HEADER.CONTENT_LENGTH]: blob.size,
                    [HTTP.HEADER.CONTENT_TYPE]: transferPart.metadata.contentType
                  }, transferPart.targetHeaders)
                });
              }), _this.options));
            } else if (targetUrl && isFileProtocol(targetUrl) && transferPart.source.url) {
              yield _awaitAsyncGenerator(retry( /*#__PURE__*/_asyncToGenerator(function* () {
                const totalSize = transferPart.metadata.contentLength;
                const response = yield streamGet(transferPart.source.url, {
                  headers: Object.assign({
                    [HTTP.HEADER.RANGE]: `${HTTP.RANGE.BYTES}=${contentRange.low}-${contentRange.high}`
                  }, transferPart.sourceHeaders)
                });
                const contentLengthStr = response.headers.get(HTTP.HEADER.CONTENT_LENGTH);
                const contentLength = Number.parseInt(contentLengthStr, 10);

                if (!Number.isFinite(contentLength)) {
                  throw Error(`Server did not respond with a Content-Length header: ${contentLengthStr}`);
                } // there have been cases where the server does not honor the range header.
                // since we'll be reading the entire response into memory, protect against accidentally
                // reading a very large amount of data into memory. For example, if the server responds with
                // an entire 1GB file even though we only requested a portion of that file, avoid reading
                // the whole 1GB into memory.


                if (contentLength !== contentRange.length) {
                  throw Error(`Server does not seem to have respected Range header. Expected range ${contentRange.low}-${contentRange.high}, content length is ${contentLength}B`);
                }

                const buffer = yield streamToBuffer(HTTP.METHOD.GET, transferPart.source.url, response.status, response.body, contentLength);
                yield _this.randomFileAccess.write(targetUrl, contentRange, buffer, totalSize);
              }), _this.options));
            }

            controller.notify(TransferEvents.AFTER_TRANSFER, _this.name, transferPart);
            yield transferPart;
          } catch (error) {
            controller.notifyError(_this.name, error, transferPart);
          }
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return != null) {
            yield _awaitAsyncGenerator(_iterator.return());
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }
    })();
  }

}

module.exports = {
  Transfer
};